import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { Trash2, Plus, Power, X, Pencil, ArrowUp, ArrowDown, Sliders, Gauge, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["casino", "slots", "crash", "table", "fishing", "arcade", "lottery", "sports"];

const RTP_PRESETS = [
  { id: "low_88",     rtp: 88,  label: "Low 88%",     hint: "High house edge (~12%)",      color: "rose" },
  { id: "mid_92",     rtp: 92,  label: "Mid 92%",     hint: "Aggressive house edge (8%)",  color: "amber" },
  { id: "high_94",    rtp: 94,  label: "High 94%",    hint: "Balanced (6%)",               color: "yellow" },
  { id: "premium_96", rtp: 96,  label: "Premium 96%", hint: "Industry default (4%)",       color: "emerald" },
  { id: "default",    rtp: 96,  label: "Provider default", hint: "Use provider's stock RTP", color: "gray" },
];

function rtpBadgeColor(rtp) {
  if (rtp >= 96) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (rtp >= 94) return "bg-yellow-50 text-yellow-800 border-yellow-200";
  if (rtp >= 92) return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-rose-50 text-rose-800 border-rose-200";
}

const EMPTY = {
  name: "",
  slug: "",
  category: "casino",
  icon: "",
  link: "",
  game_uid: "",
  provider_code: "",
  order: 0,
  status: "active",
  target_rtp: 96,
  rtp_preset: "default",
  rtp_enforced: false,
};

export default function Providers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterCat ? `?category=${encodeURIComponent(filterCat)}` : "";
      const { data } = await api.get(`/providers${params}`);
      setRows(data);
    } catch (_) {
      /* interceptor */
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/providers/rtp-summary");
      setSummary(data);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat]);

  useEffect(() => {
    loadSummary();
  }, []);

  const counts = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      m[r.category] = (m[r.category] || 0) + 1;
    });
    return m;
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, category: filterCat || "casino" });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      slug: row.slug || "",
      category: row.category || "casino",
      icon: row.icon || "",
      link: row.link || "",
      game_uid: row.game_uid || "",
      provider_code: row.provider_code || "",
      order: typeof row.order === "number" ? row.order : 0,
      status: row.status || "active",
      target_rtp: typeof row.target_rtp === "number" ? row.target_rtp : 96,
      rtp_preset: row.rtp_preset || "default",
      rtp_enforced: !!row.rtp_enforced,
    });
    setShowForm(true);
  };

  const close = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        target_rtp: Math.max(80, Math.min(99, Number(form.target_rtp) || 96)),
      };
      if (editing) {
        await api.patch(`/providers/${editing.id}`, payload);
        toast.success("Provider updated");
      } else {
        await api.post("/providers", payload);
        toast.success("Provider created");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const newStatus = row.status === "active" ? "inactive" : "active";
    const slug = (row.slug || "").trim();
    const siblings = slug ? rows.filter((r) => r.slug === slug && r.id !== row.id) : [];

    // If duplicates exist, ask the admin whether to apply to all (so the
    // public-site tile actually flips visibility, not just one of N rows).
    let idsToUpdate = [row.id];
    if (siblings.length > 0) {
      const sibCats = siblings.map((s) => s.category).join(", ");
      const action = newStatus === "active" ? "enable" : "disable";
      const apply = window.confirm(
        `"${row.name}" (slug: ${slug}) also exists in ${siblings.length} other ${siblings.length === 1 ? "category" : "categories"}: ${sibCats}.\n\nClick OK to ${action} in ALL ${siblings.length + 1} categories (so the public site reflects the change).\n\nClick Cancel to ${action} only this row.`
      );
      if (apply) idsToUpdate = [row.id, ...siblings.map((s) => s.id)];
    }

    try {
      await Promise.all(
        idsToUpdate.map((id) => api.patch(`/providers/${id}`, { status: newStatus }))
      );
      toast.success(
        idsToUpdate.length > 1
          ? `${newStatus === "active" ? "Enabled" : "Disabled"} in ${idsToUpdate.length} categories`
          : `Provider ${newStatus === "active" ? "enabled" : "disabled"}`
      );
      load();
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const onDelete = async (row) => {
    // Find other rows with the same slug (if any) — a provider can be split
    // across multiple categories; deleting just one row leaves siblings behind
    // and the public carousel keeps showing the tile (it dedupes by slug).
    const slug = (row.slug || "").trim();
    const siblings = slug ? rows.filter((r) => r.slug === slug && r.id !== row.id) : [];

    let idsToDelete = [row.id];
    let confirmMsg = `Delete provider "${row.name}" from ${row.category}?`;
    if (siblings.length > 0) {
      const sibCats = siblings.map((s) => s.category).join(", ");
      confirmMsg = `"${row.name}" (slug: ${slug}) also exists in ${siblings.length} other ${siblings.length === 1 ? "category" : "categories"}: ${sibCats}.\n\nClick OK to delete from ALL ${siblings.length + 1} categories (recommended — removes the tile from the public site).\n\nClick Cancel to keep all rows.`;
      if (!window.confirm(confirmMsg)) return;
      idsToDelete = [row.id, ...siblings.map((s) => s.id)];
    } else {
      if (!window.confirm(confirmMsg)) return;
    }

    try {
      await Promise.all(idsToDelete.map((id) => api.delete(`/providers/${id}`)));
      toast.success(
        idsToDelete.length > 1
          ? `Deleted from ${idsToDelete.length} categories`
          : "Provider deleted"
      );
      load();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const move = async (row, direction) => {
    // Reorder within the currently-visible list (respects active category filter).
    // We swap the `order` value with the neighbor row, then save both via PATCH.
    const list = displayRows;
    const idx = list.findIndex((r) => r.id === row.id);
    if (idx === -1) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    const neighbor = list[targetIdx];
    // If both rows happen to share the same numeric order, bump them apart so the swap is visible.
    const myOrder = typeof row.order === "number" ? row.order : 0;
    const theirOrder = typeof neighbor.order === "number" ? neighbor.order : 0;
    const next = myOrder === theirOrder
      ? { mine: theirOrder + (direction === "up" ? -1 : 1), theirs: theirOrder }
      : { mine: theirOrder, theirs: myOrder };
    try {
      await Promise.all([
        api.patch(`/providers/${row.id}`, { order: next.mine }),
        api.patch(`/providers/${neighbor.id}`, { order: next.theirs }),
      ]);
      setRows((prev) =>
        prev.map((r) => {
          if (r.id === row.id) return { ...r, order: next.mine };
          if (r.id === neighbor.id) return { ...r, order: next.theirs };
          return r;
        })
      );
    } catch (_) {
      toast.error("Reorder failed");
    }
  };

  // Sorted-for-display: lowest `order` first, then by name as tie-breaker.
  // The public site uses the same order, so this view mirrors what users see.
  const displayRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ao = typeof a.order === "number" ? a.order : 0;
      const bo = typeof b.order === "number" ? b.order : 0;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [rows]);

  return (
    <div data-testid="providers-page">
      <PageHeader
        title="Providers / Vendors"
        subtitle="Add, edit, hide or remove provider logos shown on the public site (Casino, Slots, Crash, Table, Fishing, Arcade, Lottery, Sports). Use the ↑/↓ arrows in each row to control the order on the public Providers carousel — top of the list shows first."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulk(true)}
              className="h-9 px-3 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5"
              data-testid="provider-bulk-rtp-btn"
            >
              <Sliders size={14} /> Bulk RTP
            </button>
            <button
              onClick={openCreate}
              className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5"
              data-testid="provider-add-btn"
            >
              <Plus size={14} /> Add provider
            </button>
          </div>
        }
      />

      <RtpSummaryBanner summary={summary} />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="provider-filter-chips">
        <button
          onClick={() => setFilterCat("")}
          className={`h-8 px-3 text-xs rounded-sm border ${
            filterCat === "" ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          All ({rows.length})
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            className={`h-8 px-3 text-xs rounded-sm border capitalize ${
              filterCat === c
                ? "bg-emerald-500 text-white border-emerald-500"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`provider-filter-${c}`}
          >
            {c} {counts[c] ? `(${counts[c]})` : ""}
          </button>
        ))}
      </div>

      <DataTable
        testId="providers-table"
        loading={loading}
        rows={displayRows}
        columns={[
          {
            key: "icon",
            label: "Logo",
            render: (r) =>
              r.icon ? (
                <img
                  src={r.icon}
                  alt={r.name}
                  className="h-10 w-10 object-contain bg-[#0c0e14] rounded-sm p-1"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0.3";
                  }}
                />
              ) : (
                <div className="h-10 w-10 bg-gray-100 rounded-sm flex items-center justify-center text-xs text-gray-400">
                  —
                </div>
              ),
          },
          { key: "name", label: "Name" },
          {
            key: "category",
            label: "Category",
            render: (r) => <span className="capitalize">{r.category}</span>,
          },
          {
            key: "slug",
            label: "Slug",
            render: (r) => <span className="font-mono text-xs text-gray-700">{r.slug || "—"}</span>,
          },
          {
            key: "game_uid",
            label: "Game Code",
            render: (r) => (
              <span className="font-mono text-xs text-emerald-700">
                {r.game_uid || "—"}
              </span>
            ),
          },
          { key: "order", label: "Order" },
          {
            key: "rtp",
            label: "RTP",
            render: (r) => {
              const rtp = typeof r.target_rtp === "number" ? r.target_rtp : 96;
              const enforced = !!r.rtp_enforced;
              return (
                <div className="flex items-center gap-1.5" data-testid={`provider-rtp-cell-${r.id}`}>
                  <span className={`inline-flex items-center gap-1 h-6 px-1.5 text-[11px] font-semibold border rounded-sm tabular-nums ${rtpBadgeColor(rtp)}`}>
                    <Gauge size={10} /> {rtp.toFixed(1)}%
                  </span>
                  {enforced ? (
                    <span title="Enforced — sent to provider on game launch" className="inline-flex items-center justify-center h-5 w-5 rounded-sm border border-indigo-200 bg-indigo-50 text-indigo-700">
                      <CheckCircle2 size={10} />
                    </span>
                  ) : (
                    <span title="Reference only — not sent to provider" className="inline-flex items-center justify-center h-5 w-5 rounded-sm border border-gray-200 bg-gray-50 text-gray-400">
                      <ShieldAlert size={10} />
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            key: "status",
            label: "Status",
            render: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge>,
          },
        ]}
        actions={(row) => {
          const idx = displayRows.findIndex((r) => r.id === row.id);
          const isFirst = idx <= 0;
          const isLast = idx >= displayRows.length - 1;
          return (
          <div className="flex items-center gap-1 justify-end">
            <div className="flex flex-col gap-0.5 mr-1">
              <button
                onClick={() => move(row, "up")}
                disabled={isFirst}
                className="h-4 w-6 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-emerald-600 hover:border-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up (lower order = higher on public site)"
                data-testid={`provider-up-${row.id}`}
              >
                <ArrowUp size={10} />
              </button>
              <button
                onClick={() => move(row, "down")}
                disabled={isLast}
                className="h-4 w-6 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-emerald-600 hover:border-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down"
                data-testid={`provider-down-${row.id}`}
              >
                <ArrowDown size={10} />
              </button>
            </div>
            <button
              onClick={() => openEdit(row)}
              className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 rounded-sm text-gray-700 hover:bg-gray-50"
              data-testid={`provider-edit-${row.id}`}
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              onClick={() => toggleStatus(row)}
              className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${
                row.status === "active"
                  ? "border-amber-200 text-amber-700 hover:bg-amber-50"
                  : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              }`}
              data-testid={`provider-toggle-${row.id}`}
            >
              <Power size={12} /> {row.status === "active" ? "Disable" : "Enable"}
            </button>
            <button
              onClick={() => onDelete(row)}
              className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200"
              data-testid={`provider-delete-${row.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
          );
        }}
      />

      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="bg-white rounded-sm w-full max-w-xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="font-display font-bold">
                {editing ? "Edit provider" : "Add provider"}
              </h3>
              <button onClick={close} className="h-8 w-8 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3" data-testid="provider-form">
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Name *"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  testId="provider-name"
                  required
                />
                <Field
                  label="Slug"
                  value={form.slug}
                  onChange={(v) => setForm({ ...form, slug: v })}
                  testId="provider-slug"
                  placeholder="e.g. evo, pg, jili"
                  hint="Short identifier (auto-used as id on the site)."
                  mono
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm capitalize"
                    data-testid="provider-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} className="capitalize">
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <Field
                  label="Order"
                  type="number"
                  value={form.order}
                  onChange={(v) => setForm({ ...form, order: v })}
                  testId="provider-order"
                />
              </div>

              <Field
                label="Logo / Icon URL"
                value={form.icon}
                onChange={(v) => setForm({ ...form, icon: v })}
                testId="provider-icon"
                placeholder="https://…/vendor-logo.png"
              />
              {form.icon && (
                <div className="flex items-center gap-3 p-2 border border-gray-100 rounded-sm bg-gray-50">
                  <img
                    src={form.icon}
                    alt=""
                    className="h-12 w-12 object-contain bg-[#0c0e14] rounded p-1"
                  />
                  <span className="text-xs text-gray-500">Preview</span>
                </div>
              )}

              <Field
                label="Deep Link (optional)"
                value={form.link}
                onChange={(v) => setForm({ ...form, link: v })}
                testId="provider-link"
                placeholder="/casino/evolution"
              />

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Game Code (game_uid)"
                  value={form.game_uid}
                  onChange={(v) => setForm({ ...form, game_uid: v })}
                  testId="provider-game-uid"
                  placeholder="e.g. SLOT_PG_001"
                  hint="Sent to DST on tile click. Leave blank to use slug."
                  mono
                />
                <Field
                  label="Provider Code"
                  value={form.provider_code}
                  onChange={(v) => setForm({ ...form, provider_code: v })}
                  testId="provider-code"
                  placeholder="e.g. PG, JILI, JDB"
                  hint="Vendor short-code as defined by the DST aggregator."
                  mono
                />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                  data-testid="provider-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <RtpSection form={form} setForm={setForm} />

              <button
                type="submit"
                disabled={saving}
                className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold rounded-sm text-sm"
                data-testid="provider-submit"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create provider"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showBulk && (
        <BulkRtpModal
          onClose={() => setShowBulk(false)}
          afterApply={() => { setShowBulk(false); load(); loadSummary(); }}
        />
      )}
    </div>
  );
}

function RtpSummaryBanner({ summary }) {
  if (!summary) return null;
  const { total, avg_rtp, enforced, by_category } = summary;
  return (
    <div className="bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border border-indigo-200 rounded-sm p-4 mb-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="rtp-summary-banner">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-sm bg-indigo-500 text-white inline-flex items-center justify-center">
          <Gauge size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Platform Avg. RTP</div>
          <div className="font-display text-xl font-bold tabular text-gray-900">{(avg_rtp || 0).toFixed(2)}%</div>
        </div>
      </div>
      <div className="h-10 w-px bg-gray-200" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Providers</div>
        <div className="font-display text-base font-bold text-gray-800">
          {total} <span className="text-xs text-gray-500 font-normal">total · {enforced} enforced</span>
        </div>
      </div>
      <div className="h-10 w-px bg-gray-200" />
      <div className="flex flex-wrap gap-1.5 flex-1 min-w-[280px]">
        {Object.entries(by_category || {}).map(([cat, v]) => (
          <span
            key={cat}
            className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(v.avg_rtp)}`}
            title={`${v.count} provider(s), ${v.enforced} enforced`}
          >
            <span className="capitalize font-semibold">{cat}</span>
            <span className="tabular-nums">{(v.avg_rtp || 0).toFixed(1)}%</span>
            <span className="text-gray-500">·{v.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function RtpSection({ form, setForm }) {
  const houseEdge = (100 - (Number(form.target_rtp) || 0)).toFixed(2);
  const applyPreset = (p) => {
    setForm({ ...form, rtp_preset: p.id, target_rtp: p.rtp });
  };
  return (
    <div className="border-t border-gray-200 pt-3 mt-1" data-testid="provider-rtp-section">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
        <Gauge size={12} className="text-indigo-500" /> Target RTP / House Edge
      </div>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="provider-rtp-presets">
        {RTP_PRESETS.map((p) => {
          const active = form.rtp_preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              title={p.hint}
              data-testid={`provider-rtp-preset-${p.id}`}
              className={`h-8 px-2.5 text-[11px] rounded-sm border font-semibold inline-flex items-center gap-1 ${
                active
                  ? rtpBadgeColor(p.rtp) + " ring-2 ring-offset-1 ring-indigo-300"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <Gauge size={10} /> {p.label}
            </button>
          );
        })}
      </div>

      {/* Slider + numeric */}
      <div className="grid grid-cols-[1fr_auto] items-center gap-3 mb-2">
        <div>
          <input
            type="range"
            min="80"
            max="99"
            step="0.5"
            value={form.target_rtp}
            onChange={(e) => setForm({ ...form, target_rtp: Number(e.target.value), rtp_preset: "custom" })}
            className="w-full accent-indigo-500"
            data-testid="provider-rtp-slider"
          />
          <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
            <span>80%</span><span>85%</span><span>90%</span><span>92%</span><span>94%</span><span>96%</span><span>99%</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <input
            type="number"
            min="80"
            max="99"
            step="0.1"
            value={form.target_rtp}
            onChange={(e) => setForm({ ...form, target_rtp: Number(e.target.value), rtp_preset: "custom" })}
            className="w-20 h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono text-right focus:outline-none focus:border-indigo-500"
            data-testid="provider-rtp-input"
          />
          <span className="text-sm text-gray-500 font-semibold">%</span>
        </div>
      </div>

      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 mb-3">
        <div className="text-xs text-gray-600">
          Player payout: <b className="text-emerald-700">{Number(form.target_rtp).toFixed(2)}%</b> · House edge: <b className="text-rose-700">{houseEdge}%</b>
        </div>
        <span className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(Number(form.target_rtp))}`}>
          <Gauge size={10} /> {Number(form.target_rtp).toFixed(1)}%
        </span>
      </div>

      <label className="flex items-start gap-2.5 p-2.5 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
        <input
          type="checkbox"
          checked={!!form.rtp_enforced}
          onChange={(e) => setForm({ ...form, rtp_enforced: e.target.checked })}
          className="mt-0.5 accent-indigo-500"
          data-testid="provider-rtp-enforced"
        />
        <div>
          <div className="text-[12px] font-semibold text-gray-800 inline-flex items-center gap-1.5">
            <ShieldAlert size={12} className="text-indigo-500" />
            Enforce on game launch
          </div>
          <div className="text-[10px] text-gray-500 leading-snug mt-0.5">
            When enabled, this RTP variant is sent to the provider on game launch (only takes effect for providers whose API supports RTP variants — e.g., Pragmatic Play, Spinomenal, Habanero). For others, this value is stored as a reference target for compliance reporting.
          </div>
        </div>
      </label>
    </div>
  );
}

function BulkRtpModal({ onClose, afterApply }) {
  const [scope, setScope] = useState("");           // "" = all, else category
  const [preset, setPreset] = useState("mid_92");
  const [rtp, setRtp] = useState(92);
  const [enforced, setEnforced] = useState(true);
  const [busy, setBusy] = useState(false);

  const applyPreset = (p) => { setPreset(p.id); setRtp(p.rtp); };

  const apply = async () => {
    if (!window.confirm(`Apply ${rtp}% RTP to ${scope ? `ALL ${scope.toUpperCase()} providers` : "ALL providers"}?\nThis overrides each provider's current setting.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/providers/bulk-rtp", {
        category: scope || null,
        target_rtp: rtp,
        rtp_preset: preset,
        rtp_enforced: enforced,
      });
      toast.success(`Applied ${rtp}% RTP to ${data.modified} provider${data.modified === 1 ? "" : "s"}`);
      afterApply?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk apply failed");
    } finally {
      setBusy(false);
    }
  };

  const houseEdge = (100 - rtp).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-rtp-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-display font-bold inline-flex items-center gap-1.5">
            <Sliders size={16} className="text-indigo-500" /> Bulk apply RTP / House Edge
          </h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-600 leading-relaxed bg-indigo-50 border border-indigo-100 rounded-sm p-3">
            Set a single RTP target across all providers — or only one category (e.g., <b>slots</b>). Useful when an operator wants to dial back overall payout in response to GGR targets. Only providers whose API supports RTP variants will receive the value at game launch; others store it as a reference.
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Apply to</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm capitalize"
              data-testid="bulk-rtp-scope"
            >
              <option value="">All providers (every category)</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c} className="capitalize">{c} only</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Preset</label>
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-rtp-presets">
              {RTP_PRESETS.filter((p) => p.id !== "default").map((p) => {
                const active = preset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPreset(p)}
                    title={p.hint}
                    data-testid={`bulk-rtp-preset-${p.id}`}
                    className={`h-9 px-3 text-xs rounded-sm border font-semibold inline-flex items-center gap-1.5 ${
                      active
                        ? rtpBadgeColor(p.rtp) + " ring-2 ring-offset-1 ring-indigo-300"
                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <Gauge size={11} /> {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Target RTP %</label>
            <div className="grid grid-cols-[1fr_auto] items-center gap-3">
              <input
                type="range"
                min="80"
                max="99"
                step="0.5"
                value={rtp}
                onChange={(e) => { setRtp(Number(e.target.value)); setPreset("custom"); }}
                className="w-full accent-indigo-500"
                data-testid="bulk-rtp-slider"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="80"
                  max="99"
                  step="0.1"
                  value={rtp}
                  onChange={(e) => { setRtp(Number(e.target.value)); setPreset("custom"); }}
                  className="w-20 h-9 px-2 border border-gray-200 rounded-sm text-sm font-mono text-right"
                  data-testid="bulk-rtp-input"
                />
                <span className="text-sm text-gray-500 font-semibold">%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
            <div className="text-xs text-gray-600">
              Payout: <b className="text-emerald-700">{rtp.toFixed(2)}%</b> · House edge: <b className="text-rose-700">{houseEdge}%</b>
            </div>
            <span className={`inline-flex items-center gap-1 h-6 px-2 text-[11px] rounded-sm border ${rtpBadgeColor(rtp)}`}>
              <Gauge size={10} /> {rtp.toFixed(1)}%
            </span>
          </div>

          <label className="flex items-start gap-2.5 p-2.5 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={enforced}
              onChange={(e) => setEnforced(e.target.checked)}
              className="mt-0.5 accent-indigo-500"
              data-testid="bulk-rtp-enforced"
            />
            <div>
              <div className="text-[12px] font-semibold text-gray-800">Enforce on game launch</div>
              <div className="text-[10px] text-gray-500 leading-snug">Send the RTP variant to provider APIs that support it. Otherwise this is a reference value only.</div>
            </div>
          </label>

          <button
            type="button"
            onClick={apply}
            disabled={busy}
            data-testid="bulk-rtp-apply"
            className="w-full h-10 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-semibold rounded-sm text-sm inline-flex items-center justify-center gap-2"
          >
            {busy ? "Applying…" : <><Sliders size={14} /> Apply to {scope ? scope.toUpperCase() : "ALL"} providers</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, placeholder, hint, mono }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className={`w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 ${
          mono ? "font-mono" : ""
        }`}
        data-testid={testId}
      />
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}
